import React from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { alpha, useTheme } from "@mui/material/styles";

interface FormNavigationProps {
  currentSection: number;
  totalSections: number;
  isNavigating: boolean;
  isSaving: boolean;
  onPrevious: () => void;
  onNext: () => void;
}

export const FormNavigation: React.FC<FormNavigationProps> = ({
  currentSection,
  totalSections,
  isNavigating,
  isSaving,
  onPrevious,
  onNext,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 2,
        mt: 3,
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        pt: 3,
      }}
    >
      {/* Previous section button */}
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={onPrevious}
        disabled={currentSection === 0 || isNavigating}
        sx={{
          borderRadius: 2,
          textTransform: "none",
          fontWeight: 600,
          minWidth: 120,
          py: 1,
          transition: "all 0.2s ease-in-out",
          "&:hover:not(:disabled)": {
            transform: "translateX(-2px)",
          },
        }}
      >
        Previous
      </Button>

      {/* Section indicator with loading indicator during save */}
      <Typography
        sx={{
          mx: 2,
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontWeight: 500,
          color: "text.secondary",
        }}
      >
        {currentSection + 1} / {totalSections}
        {(isSaving || isNavigating) && (
          <CircularProgress size={16} thickness={4} sx={{ ml: 1 }} />
        )}
      </Typography>

      {/* Next section button */}
      <Button
        variant="contained"
        endIcon={<ArrowForwardIcon />}
        onClick={onNext}
        disabled={currentSection === totalSections - 1 || isNavigating}
        sx={{
          borderRadius: 2,
          textTransform: "none",
          fontWeight: 600,
          minWidth: 120,
          py: 1,
          transition: "all 0.2s ease-in-out",
          "&:hover:not(:disabled)": {
            transform: "translateX(2px)",
          },
        }}
      >
        Next
      </Button>
    </Box>
  );
};
